import { useEffect, useState } from 'react';
import { numero, t } from '../i18n/idioma';
import { nomeDoItem } from '../i18n/nomes';
import { ItemIcon } from './ItemIcon';
import type { TradeSlot, TradeState } from '../world/types';

interface Props {
  state: TradeState;
  busy?: boolean;
  onOfferGold(qty: number): void;
  onTakeBack(itemId: string): void;
  onReady(ready: boolean): void;
  onCancel(): void;
}

const SLOTS = 10;

export function TradeGump({ state, busy = false, onOfferGold, onTakeBack, onReady }: Props) {
  const [gold, setGold] = useState(String(state.mine.gold));

  useEffect(() => { setGold(String(state.mine.gold)); }, [state.mine.gold]);

  const sendGold = () => {
    const n = Math.max(0, Number.parseInt(gold, 10) || 0);
    if (n !== state.mine.gold) onOfferGold(n);
  };

  return (
    <div className="trade" role="dialog" aria-modal="true" aria-label={t('troca.titulo')}>
      <Side
        title={t('troca.voceOferece')}
        items={state.mine.items}
        gold={state.mine.gold}
        accepted={state.mine.accepted}
        goldValue={gold}
        onGoldChange={setGold}
        onGoldCommit={sendGold}
        onTakeBack={onTakeBack}
        onReady={() => onReady(!state.mine.accepted)}
        readyBusy={busy}
        mine
      />
      <Side
        title={t('troca.peerOferece', { nome: state.peer })}
        items={state.theirs.items}
        gold={state.theirs.gold}
        accepted={state.theirs.accepted}
      />
      {state.tipo === 'aposta' && <p className="duelo-nota">{t('duelo.apostaNota')}</p>}
    </div>
  );
}

function Side({
  title, items, gold, accepted, goldValue, onGoldChange, onGoldCommit, onTakeBack, onReady, readyBusy, mine
}: {
  title: string;
  items: readonly TradeSlot[];
  gold: number;
  accepted: boolean;
  goldValue?: string;
  onGoldChange?: (v: string) => void;
  onGoldCommit?: () => void;
  onTakeBack?: (id: string) => void;
  onReady?: () => void;
  readyBusy?: boolean;
  mine?: boolean;
}) {
  const cells = Array.from({ length: SLOTS }, (_, i) => items[i] ?? null);
  return (
    <div className="trade-side">
      <p className="trade-side__title">{title}</p>
      <div className="trade-grid">
        {cells.map((item, i) => (
          <button
            key={item ? `${item.id}-${i}` : `empty-${i}`}
            type="button"
            className={`trade-cell${item ? '' : ' is-empty'}`}
            disabled={!mine || !item || readyBusy}
            onClick={() => { if (item && onTakeBack) onTakeBack(item.id); }}
            title={item ? nomeDoItem(item.defId, item.name) : undefined}
          >
            {item && <ItemIcon id={item.defId} label={nomeDoItem(item.defId, item.name)} size={32} quantity={item.qty} />}
          </button>
        ))}
      </div>
      <label className="trade-gold">
        <span>{t('comum.ouro')}</span>
        {mine ? (
          <input
            inputMode="numeric"
            value={goldValue}
            onChange={e => onGoldChange?.(e.target.value.replace(/[^\d]/g, ''))}
            onBlur={onGoldCommit}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onGoldCommit?.(); } }}
            disabled={readyBusy}
          />
        ) : (
          <span className="trade-gold__val">{numero(gold)}</span>
        )}
      </label>
      {mine ? (
        <button
          type="button"
          className={`btn${accepted ? ' btn--primary' : ''}`}
          disabled={readyBusy}
          aria-pressed={accepted}
          onClick={onReady}
        >
          {t('comum.aceitar')}
        </button>
      ) : (
        <p className={`trade-ready${accepted ? ' is-on' : ''}`}>{accepted ? t('troca.aceitou') : t('troca.aguardando')}</p>
      )}
    </div>
  );
}
